import CameraIcon from "assets/images/CameraIcon";
import Trash from "assets/images/Trash";
import locationPinIcon from "assets/images/location-pin-small.svg";
import {format} from "date-fns";
import {Fragment, useMemo, useState} from "react";
import {common as stringsCommon, insurance as strings} from "strings";

import AddInsuranceCardModal from "components/AddInsuranceCardModal";
import AlertModal from "components/AlertModal";
import Button from "components/Button";
import ConfirmDeleteInsuranceCard from "components/ConfirmDeleteInsuranceCard";
import Divider from "components/Divider";
import LoadingIndicator from "components/LoadingIndicator";
import MobileNavigationHeader from "components/MobileNavigationHeader";
import PageTitle from "components/PageTitle";
import Select from "components/Select";

import * as models from "liberty-core/api/models";
import {
  useDeleteInsuranceCard,
  useGetInsuranceCards,
  useUploadInsuranceCard,
  useGetInsuranceCardTypes,
} from "liberty-core/api/react-query/insurance";

import {useAuthenticatedUser} from "lib/auth";
import useCurrentPatientId from "lib/useCurrentPatientId";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./InsurancePage.module.css";

const DEFAULT_PATIENT_ID = "defaultPatientId";

export default function InsurancePage() {
  const [pharmacy] = useCurrentPharmacy();
  const [authenticatedUser] = useAuthenticatedUser();
  const {selectedPatientId, updateSelectedPatientId} = useCurrentPatientId(
    authenticatedUser?.data.linkedPatients ?? [],
  );
  const {data: cardList, isLoading: isLoadingCards} =
    useGetInsuranceCards(selectedPatientId);
  const {data: cardTypes, isLoading: isLoadingCardTypes} =
    useGetInsuranceCardTypes();

  const [cardToDelete, setCardToDelete] = useState<null | models.InsuranceCard>(
    null,
  );
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showGenericError, setShowGenericError] = useState(false);

  const {mutateAsync: saveInsuranceCard, isLoading: isSaving} =
    useUploadInsuranceCard();
  const {mutateAsync: deleteInsuranceCard, isLoading: isDeleting} =
    useDeleteInsuranceCard();

  const onAddCard = (frontImage: File, backImage: File, cardType: number) => {
    if (isSaving) {
      return;
    }

    saveInsuranceCard({
      frontImage: frontImage,
      backImage: backImage,
      cardType,
      patientId: selectedPatientId,
    })
      .then(() => {
        setShowAddCardModal(false);
        setShowSuccessModal(true);
      })
      .catch(() => {
        setShowGenericError(true);
      });
  };

  const onDelete = () => {
    if (isDeleting || !cardToDelete) {
      return;
    }

    deleteInsuranceCard(cardToDelete.id)
      .then(() => {
        setCardToDelete(null);
        setShowSuccessModal(true);
      })
      .catch(() => setShowGenericError(true));
  };

  const patientNameOptions = useMemo(() => {
    if (!authenticatedUser?.data.linkedPatients?.length) {
      return [];
    }

    return [
      {
        value: DEFAULT_PATIENT_ID,
        label: authenticatedUser.data.name,
      },
      ...authenticatedUser.data.linkedPatients.map((p) => ({
        value: p.patientId,
        label: `${p.firstName} ${p.lastName}`,
      })),
    ].sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
  }, [authenticatedUser]);

  type PhotoData = {
    url: string;
    uploadDate: Date;
  };

  type InsuranceCardPhotoProps = {
    label: string;
  } & PhotoData;

  function InsuranceCardPhoto({
    label,
    url,
    uploadDate,
  }: InsuranceCardPhotoProps) {
    return (
      <div className={styles.cardPhotoContainer}>
        <img className={styles.cardPhoto} alt={label} src={url} />
        <h2 className={styles.label}>{label}</h2>
        <span className={styles.date}>{format(uploadDate, "MM/dd/yyyy")}</span>
      </div>
    );
  }

  function renderInsuranceCard(card: models.InsuranceCard) {
    return (
      <div key={card.id} className={styles.insuranceCard}>
        <div className={styles.cardHeader}>
          <h2 className={styles.label}>{card.description}</h2>
          <button
            className={styles.deleteButton}
            onClick={() => setCardToDelete(card)}
          >
            <Trash />
          </button>
        </div>
        <div className={styles.cardRow}>
          <InsuranceCardPhoto
            label={strings.front}
            url={card.frontImage}
            uploadDate={card.uploadedDate}
          />
          <InsuranceCardPhoto
            label={strings.back}
            url={card.backImage}
            uploadDate={card.uploadedDate}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <MobileNavigationHeader backTo="/profile" />
      <PageTitle className={styles.title}>{strings.title}</PageTitle>
      <div>
        <img src={locationPinIcon} alt="" />
        <span className={styles.subtitle}>{pharmacy?.name}</span>
      </div>
      <h3 className={styles.description}>{strings.description}</h3>
      <Select
        className={styles.patientNameSelect}
        value={selectedPatientId ?? DEFAULT_PATIENT_ID}
        options={patientNameOptions}
        disabled={!authenticatedUser?.data.linkedPatients?.length}
        onChange={(v) =>
          updateSelectedPatientId(v === DEFAULT_PATIENT_ID ? null : v)
        }
      />
      {isLoadingCards || isLoadingCardTypes ? (
        <LoadingIndicator />
      ) : cardList && cardList.length ? (
        cardList.map((card, i) => (
          <Fragment key={card.id}>
            {renderInsuranceCard(card)}
            {i !== cardList.length - 1 ? (
              <Divider className={styles.lineDivider} />
            ) : null}
          </Fragment>
        ))
      ) : (
        <div className={styles.noCardsContainer}>{strings.noCards}</div>
      )}
      <div className={styles.addButtonContainer}>
        <Button
          className={styles.button}
          variant="unfilled"
          iconLeft={(color: string) => <CameraIcon color={color} />}
          onClick={() => setShowAddCardModal(true)}
          disabled={isLoadingCards || isLoadingCardTypes}
        >
          {strings.addCard}
        </Button>
      </div>
      <ConfirmDeleteInsuranceCard
        visible={cardToDelete !== null}
        onClose={() => setCardToDelete(null)}
        onDelete={onDelete}
        loading={isDeleting}
      />
      <AddInsuranceCardModal
        visible={showAddCardModal}
        onClose={() => setShowAddCardModal(false)}
        cardTypes={cardTypes ?? []}
        onSave={onAddCard}
        isLoading={isSaving}
      />
      <AlertModal
        visible={showSuccessModal}
        onClickConfirm={() => setShowSuccessModal(false)}
        title={stringsCommon.success}
        message={strings.successBody}
      />
      <AlertModal
        visible={showGenericError}
        onClickConfirm={() => setShowGenericError(false)}
        title={stringsCommon.whoops}
        message={stringsCommon.somethingWentWrong}
      />
    </div>
  );
}
